import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TextField,
  Button,
  Stack,
  Alert,
  CircularProgress,
  LinearProgress,
  Card,
  CardContent,
  MenuItem,
  TableSortLabel,
} from '@mui/material';
import {
  Print as PrintIcon,
  PictureAsPdf as PdfIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { requestService } from '../../services/requestService';
import InvoiceService from '../../services/invoice.service';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { addLogoToPDF } from '../../utils/pdfLogoHelper';
import api from '../../api/axios';

interface ReportRow {
  date: string;
  requestNumber: string;
  vehicleMake: string;
  vehicleModel: string;
  vehicleYear: number | null;
  plateNumber: string;
  phoneNumber: string;
  locationFrom: string;
  locationTo: string;
  serviceType: string;
  issueDescription: string;
  serviceProviderName: string;
  serviceProviderId: number | null;
}

interface ServiceProvider {
  id: number;
  attributes: {
    name: string;
  };
}

type SortField = keyof ReportRow;
type SortOrder = 'asc' | 'desc';

const RequestItemsReport: React.FC = () => {
  const [reportData, setReportData] = useState<ReportRow[]>([]);
  const [filteredData, setFilteredData] = useState<ReportRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingStatus, setLoadingStatus] = useState('');
  const [error, setError] = useState<string>('');
  const [serviceProviders, setServiceProviders] = useState<ServiceProvider[]>([]);
  const [selectedProvider, setSelectedProvider] = useState<string>('all');

  const BATCH_SIZE = 15;
  
  // Sorting state
  const [sortField, setSortField] = useState<SortField>('date');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');

  // Period (Current month by default)
  const [periodStart, setPeriodStart] = useState<string>(() => {
    const date = new Date();
    date.setDate(1);
    return date.toISOString().split('T')[0];
  });
  const [periodEnd, setPeriodEnd] = useState<string>(() => {
    return new Date().toISOString().split('T')[0];
  });

  useEffect(() => {
    loadServiceProviders();
    loadReport();
  }, []);

  useEffect(() => {
    filterAndSortData();
  }, [reportData, selectedProvider, sortField, sortOrder]);

  const loadServiceProviders = async () => {
    try {
      const response = await api.get('/service-providers', {
        params: {
          'pagination[limit]': -1,
          sort: 'name:asc'
        }
      });
      setServiceProviders(response.data.data || []);
    } catch (err) {
      console.error('Error loading service providers:', err);
    }
  };

  const parseLocation = (locationText: string | null | undefined): { from: string; to: string } => {
    if (!locationText) return { from: '', to: '' };
    
    const trimmed = locationText.trim();
    
    // Try to split by tab first (most common for location pairs)
    if (trimmed.includes('\t')) {
      const parts = trimmed.split('\t').map(p => p.trim()).filter(p => p.length > 0);
      if (parts.length >= 2) {
        return { from: parts[0], to: parts[1] };
      }
    }
    
    // Try to split by multiple spaces (common delimiter)
    const spaceParts = trimmed.split(/\s{2,}/).map(p => p.trim()).filter(p => p.length > 0);
    if (spaceParts.length >= 2) {
      return { from: spaceParts[0], to: spaceParts[1] };
    }
    
    // Try common separators
    const separators = [' to ', ' -> ', ' → ', ' - ', ' | '];
    for (const sep of separators) {
      if (trimmed.toLowerCase().includes(sep.toLowerCase())) {
        const parts = trimmed.split(new RegExp(sep, 'i')).map(p => p.trim()).filter(p => p.length > 0);
        if (parts.length >= 2) {
          return { from: parts[0], to: parts[1] };
        }
      }
    }
    
    // If only one location, use it for both
    return { from: trimmed, to: trimmed };
  };

  const filterAndSortData = () => {
    let filtered = [...reportData];

    // Filter by service provider
    if (selectedProvider !== 'all') {
      filtered = filtered.filter(row => 
        row.serviceProviderId?.toString() === selectedProvider
      );
    }

    // Sort data
    filtered.sort((a, b) => {
      let aValue: any = a[sortField];
      let bValue: any = b[sortField];

      // Handle date sorting specially
      if (sortField === 'date') {
        aValue = new Date(a.date.split(' ').reverse().join('-')).getTime();
        bValue = new Date(b.date.split(' ').reverse().join('-')).getTime();
      } else if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue?.toLowerCase() || '';
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    setFilteredData(filtered);
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const loadReport = async () => {
    try {
      setLoading(true);
      setError('');
      setLoadingProgress(0);
      setLoadingStatus('Fetching requests...');

      const requestsResponse = await requestService.getAll({
        populate: {
          insuranceClient: { populate: '*' },
          invoice: { populate: '*' },
          serviceProvider: { populate: '*' },
        },
        'filters[insuranceClient][id][$notNull]': true,
        'filters[createdAt][$gte]': `${periodStart}T00:00:00.000Z`,
        'filters[createdAt][$lte]': `${periodEnd}T23:59:59.999Z`,
        'pagination[limit]': -1,
      });

      setLoadingProgress(15);
      const requests = requestsResponse.data || [];
      const totalRequests = requests.length;
      if (totalRequests === 0) {
        setReportData([]);
        setLoadingProgress(100);
        setLoadingStatus('Done');
        return;
      }

      const invoiceIds: number[] = [];
      const requestToInvoiceId = new Map<number, number>();
      requests.forEach((request: any, index: number) => {
        const attrs = request.attributes || request;
        const insuranceClient = attrs.insuranceClient?.data?.attributes ||
          attrs.insuranceClient?.data ||
          attrs.insuranceClient?.attributes ||
          attrs.insuranceClient;
        if (!insuranceClient) return;
        const invoiceId = attrs.invoice?.data?.id ||
          attrs.invoice?.data?.attributes?.id ||
          attrs.invoice?.id ||
          attrs.invoice;
        if (invoiceId) {
          invoiceIds.push(invoiceId);
          requestToInvoiceId.set(request.id, invoiceId);
        }
      });

      const uniqueInvoiceIds = Array.from(new Set(invoiceIds));
      const itemsByInvoiceId = new Map<number, any[]>();
      const progressStart = 15;
      const progressSpan = 70;

      for (let i = 0; i < uniqueInvoiceIds.length; i += BATCH_SIZE) {
        const batch = uniqueInvoiceIds.slice(i, i + BATCH_SIZE);
        const results = await Promise.all(
          batch.map((id) =>
            InvoiceService.getItems(id).catch(() => ({ data: { data: [] } }))
          )
        );
        batch.forEach((id, j) => {
          const allItems = results[j]?.data?.data || [];
          const mainItems = allItems.filter((item: any) => {
            const itemAttrs = item.attributes || item;
            const description = (itemAttrs.description || '').toLowerCase();
            return !description.includes('extra kilometer') &&
              !description.includes('extra km') &&
              !description.includes('extra kilometers');
          });
          itemsByInvoiceId.set(id, mainItems);
        });
        const pct = Math.min(
          progressStart + Math.round((progressSpan * (i + batch.length)) / uniqueInvoiceIds.length),
          85
        );
        setLoadingProgress(pct);
        setLoadingStatus(`Loading invoice items... ${Math.min(i + batch.length, uniqueInvoiceIds.length)} of ${uniqueInvoiceIds.length}`);
      }

      setLoadingProgress(90);
      setLoadingStatus('Building report...');

      const reportRows: ReportRow[] = [];
      for (const request of requests) {
        const attrs = request.attributes || request;
        const insuranceClient = attrs.insuranceClient?.data?.attributes ||
          attrs.insuranceClient?.data ||
          attrs.insuranceClient?.attributes ||
          attrs.insuranceClient;
        if (!insuranceClient) continue;

        const serviceProvider = attrs.serviceProvider?.data?.attributes ||
          attrs.serviceProvider?.data ||
          attrs.serviceProvider?.attributes ||
          attrs.serviceProvider;
        const serviceProviderId = attrs.serviceProvider?.data?.id ||
          attrs.serviceProvider?.id || null;
        const serviceProviderName = serviceProvider?.name || 'Not Assigned';

        const invoiceId = requestToInvoiceId.get(request.id);
        let mainInvoiceItem: any = null;
        if (invoiceId) {
          const mainItems = itemsByInvoiceId.get(invoiceId) || [];
          if (mainItems.length > 0) mainInvoiceItem = mainItems[0];
        }

        const locations = parseLocation(attrs.locationText);
        const itemAttrs = mainInvoiceItem?.attributes || mainInvoiceItem;

        reportRows.push({
          date: attrs.createdAt ? new Date(attrs.createdAt).toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
          }) : '',
          requestNumber: attrs.requestNumber || `R-${request.id}`,
          vehicleMake: insuranceClient.carMake || '',
          vehicleModel: insuranceClient.carModel || '',
          vehicleYear: insuranceClient.carYear || null,
          plateNumber: insuranceClient.plateNumber || '',
          phoneNumber: insuranceClient.phoneNumber || attrs.phoneNumber || '',
          locationFrom: locations.from,
          locationTo: locations.to,
          serviceType: attrs.serviceType || itemAttrs?.description || '',
          issueDescription: attrs.description || '',
          serviceProviderName: serviceProviderName,
          serviceProviderId: serviceProviderId,
        });
      }

      setReportData(reportRows);
      setLoadingProgress(100);
      setLoadingStatus('Done');
    } catch (err: any) {
      console.error('Error loading report:', err);
      setError('Failed to load report. Please try again.');
    } finally {
      setLoading(false);
      setLoadingProgress(0);
      setLoadingStatus('');
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = async () => {
    const doc = new jsPDF('landscape'); // Landscape for wider table
    
    // Load and add logo
    await addLogoToPDF(doc);
    
    // Company header
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('DARB NATIONAL SERVICE LLC', 105, 20, { align: 'center' });
    
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text('P.O Box 417, PC 100, Muscat, Sultanate of Oman', 105, 28, { align: 'center' });
    doc.text('Phone: +968 24790888 | Email: info@darbns.com', 105, 34, { align: 'center' });
    
    // Title
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('Request Items Report', 105, 45, { align: 'center' });
    
    // Period
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(
      `Period: ${formatDate(periodStart)} - ${formatDate(periodEnd)}`,
      105,
      52,
      { align: 'center' }
    );

    // Prepare table data
    const tableData = filteredData.map((row) => [
      row.date,
      row.requestNumber,
      `${row.vehicleMake} ${row.vehicleModel}`.trim(),
      row.vehicleYear?.toString() || '',
      row.plateNumber,
      row.phoneNumber,
      row.locationFrom,
      row.locationTo,
      row.serviceProviderName,
      row.serviceType,
      row.issueDescription,
    ]);

    const headers = [
      'Date',
      'Request #',
      'Vehicle',
      'Year',
      'Plate',
      'Phone',
      'From',
      'To',
      'Service Provider',
      'Service Type',
      'Issue Description',
    ];

    autoTable(doc, {
      startY: 60,
      head: [headers],
      body: tableData,
      theme: 'grid',
      headStyles: {
        fillColor: [0, 0, 0],
        textColor: 255,
        fontStyle: 'bold',
        fontSize: 8,
      },
      styles: {
        fontSize: 7,
        cellPadding: 2,
      },
      columnStyles: {
        0: { cellWidth: 22 }, // Date
        1: { cellWidth: 25 }, // Request #
        2: { cellWidth: 30 }, // Vehicle
        3: { cellWidth: 12 }, // Year
        4: { cellWidth: 20 }, // Plate
        5: { cellWidth: 25 }, // Phone
        6: { cellWidth: 25 }, // From
        7: { cellWidth: 25 }, // To
        8: { cellWidth: 30 }, // Service Provider
        9: { cellWidth: 35 }, // Service Type
        10: { cellWidth: 40 }, // Issue Description
      },
    });

    const fileName = `Request-Items-Report-${formatDate(periodStart)}-${formatDate(periodEnd)}.pdf`;
    doc.save(fileName);
  };

  return (
      <Box>
        <style>
          {`
            @media print {
              .no-print {
                display: none !important;
              }
              @page {
                margin: 1cm;
              }
            }
          `}
        </style>

        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3} className="no-print">
          <Typography variant="h4">Request Items Report</Typography>
          <Stack direction="row" spacing={2}>
            <Button variant="outlined" startIcon={<PdfIcon />} onClick={handleDownloadPDF}>
              Download PDF
            </Button>
            <Button variant="outlined" startIcon={<PrintIcon />} onClick={handlePrint}>
              Print
            </Button>
          </Stack>
        </Stack>

        {/* Period Filters */}
        <Paper sx={{ p: 3, mb: 3 }} className="no-print">
          <Typography variant="h6" gutterBottom>
            Select Period & Filters
          </Typography>
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
            <Stack direction="row" spacing={1}>
              <TextField
                label="Start Date"
                type="date"
                size="small"
                value={periodStart}
                onChange={(e) => setPeriodStart(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="End Date"
                type="date"
                size="small"
                value={periodEnd}
                onChange={(e) => setPeriodEnd(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Stack>
            <TextField
              select
              label="Service Provider"
              size="small"
              value={selectedProvider}
              onChange={(e) => setSelectedProvider(e.target.value)}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="all">All Providers</MenuItem>
              {serviceProviders.map((provider) => (
                <MenuItem key={provider.id} value={provider.id.toString()}>
                  {provider.attributes.name}
                </MenuItem>
              ))}
            </TextField>
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={loadReport}
              disabled={loading}
            >
              {loading ? 'Loading...' : 'Generate Report'}
            </Button>
          </Stack>
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ minHeight: 280 }}>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
              {loadingStatus}
              {loadingProgress > 0 && loadingProgress < 100 && (
                <Typography component="span" variant="body2" color="primary.main" fontWeight={600} sx={{ ml: 1 }}>
                  {loadingProgress}%
                </Typography>
              )}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={loadingProgress}
              sx={{ height: 10, borderRadius: 1, mb: 2 }}
            />
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <CircularProgress size={48} sx={{ opacity: 0.7 }} />
            </Box>
          </Box>
        ) : (
          <>
            {/* Summary Card */}
            <Card sx={{ mb: 3 }} className="no-print">
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Records
                </Typography>
                <Typography variant="h4">{filteredData.length}</Typography>
                <Typography variant="caption" color="textSecondary">
                  {formatDate(periodStart)} - {formatDate(periodEnd)}
                  {selectedProvider !== 'all' && ' (Filtered)'}
                </Typography>
              </CardContent>
            </Card>

            {/* Report Table */}
            <Paper>
              <Box sx={{ p: 3 }}>
                <Typography variant="h5" align="center" gutterBottom sx={{ fontWeight: 'bold', mb: 1 }}>
                  Request Items Report
                </Typography>
                <Typography variant="body2" align="center" color="textSecondary" sx={{ mb: 3 }}>
                  Period: {formatDate(periodStart)} - {formatDate(periodEnd)}
                </Typography>

                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                        <TableCell sx={{ fontWeight: 'bold' }}>
                          <TableSortLabel
                            active={sortField === 'date'}
                            direction={sortField === 'date' ? sortOrder : 'asc'}
                            onClick={() => handleSort('date')}
                          >
                            Date
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>
                          <TableSortLabel
                            active={sortField === 'requestNumber'}
                            direction={sortField === 'requestNumber' ? sortOrder : 'asc'}
                            onClick={() => handleSort('requestNumber')}
                          >
                            Request #
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>
                          <TableSortLabel
                            active={sortField === 'vehicleMake'}
                            direction={sortField === 'vehicleMake' ? sortOrder : 'asc'}
                            onClick={() => handleSort('vehicleMake')}
                          >
                            Vehicle
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>
                          <TableSortLabel
                            active={sortField === 'vehicleYear'}
                            direction={sortField === 'vehicleYear' ? sortOrder : 'asc'}
                            onClick={() => handleSort('vehicleYear')}
                          >
                            Year
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>
                          <TableSortLabel
                            active={sortField === 'plateNumber'}
                            direction={sortField === 'plateNumber' ? sortOrder : 'asc'}
                            onClick={() => handleSort('plateNumber')}
                          >
                            Plate
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>
                          <TableSortLabel
                            active={sortField === 'phoneNumber'}
                            direction={sortField === 'phoneNumber' ? sortOrder : 'asc'}
                            onClick={() => handleSort('phoneNumber')}
                          >
                            Phone
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>
                          <TableSortLabel
                            active={sortField === 'locationFrom'}
                            direction={sortField === 'locationFrom' ? sortOrder : 'asc'}
                            onClick={() => handleSort('locationFrom')}
                          >
                            From
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>
                          <TableSortLabel
                            active={sortField === 'locationTo'}
                            direction={sortField === 'locationTo' ? sortOrder : 'asc'}
                            onClick={() => handleSort('locationTo')}
                          >
                            To
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>
                          <TableSortLabel
                            active={sortField === 'serviceProviderName'}
                            direction={sortField === 'serviceProviderName' ? sortOrder : 'asc'}
                            onClick={() => handleSort('serviceProviderName')}
                          >
                            Service Provider
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>
                          <TableSortLabel
                            active={sortField === 'serviceType'}
                            direction={sortField === 'serviceType' ? sortOrder : 'asc'}
                            onClick={() => handleSort('serviceType')}
                          >
                            Service Type
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>
                          <TableSortLabel
                            active={sortField === 'issueDescription'}
                            direction={sortField === 'issueDescription' ? sortOrder : 'asc'}
                            onClick={() => handleSort('issueDescription')}
                          >
                            Issue Description
                          </TableSortLabel>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredData.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                            <Typography color="textSecondary">
                              {selectedProvider !== 'all' 
                                ? 'No data found for the selected service provider and period.'
                                : 'No data found for the selected period.'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredData.map((row, index) => (
                          <TableRow key={index}>
                            <TableCell>{row.date}</TableCell>
                            <TableCell>{row.requestNumber}</TableCell>
                            <TableCell>
                              {row.vehicleMake} {row.vehicleModel}
                            </TableCell>
                            <TableCell>{row.vehicleYear || '-'}</TableCell>
                            <TableCell>{row.plateNumber || '-'}</TableCell>
                            <TableCell>{row.phoneNumber || '-'}</TableCell>
                            <TableCell>{row.locationFrom || '-'}</TableCell>
                            <TableCell>{row.locationTo || '-'}</TableCell>
                            <TableCell>{row.serviceProviderName || '-'}</TableCell>
                            <TableCell>{row.serviceType || '-'}</TableCell>
                            <TableCell>{row.issueDescription || '-'}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Paper>
          </>
        )}
      </Box>
  );
};

export default RequestItemsReport;

